import {Component, Input} from "@angular/core";
import {UntypedFormGroup} from "@angular/forms";
import {InsuranceProduct} from "src/app/clearpath-module/models";
import {pathOr} from "ramda";
import {MatDialog} from "@angular/material/dialog";
import {CustomPriceDialogComponent} from "../dialogs/custom-price-dialog/custom-price-dialog.component";

@Component({
  selector: "app-insurance-product-selector",
  templateUrl: "./insurance-product-selector.component.html",
  styleUrls: ["./insurance-product-selector.component.scss"]
})
export class InsuranceProductSelectorComponent {
  editing = false;

  constructor(
    private dialog: MatDialog
  ) {
  }

  get checkBoxColor(): string {
    const {accept} = this.parentForm.get(this.productKey).value;
    if (accept) {
      return this.disabled ? "app-bg-neutral" : "app-bg-success";
    }
    return "";
  }

  @Input() product: InsuranceProduct;
  @Input() productKey: string;
  @Input() selectedDealTerm: number;
  @Input() useClosestTerm: boolean;
  @Input() parentForm: UntypedFormGroup;
  @Input() productPrice: number;
  @Input() disabled: boolean;
  @Input() verified: boolean;
  @Input() unverified: boolean;
  @Input() submitParentForm;
  @Input() verifyInsuranceProduct;
  selectedTerm: number;
  pathOr = pathOr;

  editValue() {
    let originalPrice = 0;
    if (this?.product?.termCosts?.length) {
      const selectedTerm= this.parentForm.get(this.productKey).value.selectedTerm;
      originalPrice = this.product.termCosts[selectedTerm].price;
    }
    const formGroup = this.parentForm.get(this.productKey);
    const dialogRef = this.dialog.open(CustomPriceDialogComponent, {
      width: '500px',
      autoFocus: true,
      data: {
        title: 'Override ' + this.product.name + ' Price',
        originalPrice,
        customPrice: this.parentForm.get(this.productKey).get('priceOverride').value,
        maxPrice: 10000
      }
    });
    dialogRef.beforeClosed().subscribe(result => {
      if (result?.action === 'remove') {
        const formGroup = this.parentForm.get(this.productKey);
        formGroup.patchValue({priceOverride: null});
      } else if (result?.action === 'update') {
        formGroup.patchValue({priceOverride: result.value});
      }
      this.parentForm.markAsDirty();
      this.submitParentForm();
    });
  }

  touchedInvalid(): boolean {
    if (!this.parentForm) {
      return;
    }
    const productKey = this.productKey;
    const formGroup = this.parentForm.get(productKey);
    const priceOverrideCtrl = formGroup.get("priceOverride");
    if (priceOverrideCtrl) {
      return priceOverrideCtrl.touched && priceOverrideCtrl.invalid;
    }
  }

  onToggleAccept() {
    const productKey = this.productKey;
    const formGroup = this.parentForm.get(productKey);

    formGroup.patchValue({accept: !formGroup.value.accept});
    this.parentForm.markAsDirty();
    this.submitParentForm();
  }

  isUnlimitedMiles(miles: number) {
    return miles >= 999999 || miles === 0;
  }

  hasProductNames(): boolean {
    return this.product?.termCosts?.some(tc => tc.productName) || false;
  }

  getSelectedProductName(): string | null {
    if (!this.parentForm || this.productKey !== 'VSC' || !this.hasProductNames()) {
      return null;
    }

    const selectedIndex = this.parentForm.get(this.productKey).value.selectedTerm;
    if (selectedIndex === null || selectedIndex === undefined || !this.product?.termCosts?.[selectedIndex]) {
      return null;
    }

    return this.product.termCosts[selectedIndex].productName || null;
  }

  getTermDisplay(termMonths: number): string {
    const years = termMonths / 12;
    return years % 1 === 0 ? `${years} Years` : `${termMonths} Months`;
  }

  getGroupedTermCosts(): { productName: string; termCosts: { termCost: any; index: number }[] }[] {
    if (!this.product?.termCosts) {
      return [];
    }

    const groups = new Map<string, { termCost: any; index: number }[]>();

    this.product.termCosts.forEach((termCost, index) => {
      const productName = termCost.productName || 'Other';

      // Skip items with 'Other' as productName
      if (productName === 'Other') {
        return;
      }

      let groupKey = productName;

      // Extract deductible from product name and create enhanced group key
      const deductibleMatch = productName.match(/\$(\d+)\s*Deductible/i);
      if (deductibleMatch) {
        // Product already has deductible in name, use as-is
        groupKey = productName;
      } else if (productName.toLowerCase().includes('premier') || productName.toLowerCase().includes('standard')) {
        // For Premier and Standard products without explicit deductible, check the actual deductible value
        const deductible = termCost.deductible;
        if (deductible !== undefined && deductible !== null && deductible > 0) {
          // Add deductible to the group name
          groupKey = `${productName} - $${deductible} Deductible`;
        }
      }

      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      groups.get(groupKey).push({ termCost, index });
    });

    // Custom sort function for VSC products based on vehicle condition
    const sortVSCProducts = (a: string, b: string): number => {
      // Define sort order based on product names
      // This will need to be adjusted based on actual vehicle condition
      // For now, we'll implement a general sort that works for all conditions

      const getProductPriority = (productName: string): number => {
        // Normalize the product name for comparison
        const normalized = productName.toLowerCase();

        // Priority order (lower number = higher priority)
        if (normalized.includes('premier') && normalized.includes('deductible waiver') && !normalized.includes('suspension') && !normalized.includes('flex')) return 1;
        if (normalized.includes('premier flex') && normalized.includes('deductible waiver') && !normalized.includes('certified')) return 2;
        if (normalized.includes('premier') && normalized.includes('suspension') && normalized.includes('deductible waiver')) return 3;
        if (normalized.includes('premier flex certified') && normalized.includes('deductible waiver')) return 4;
        if (normalized.includes('premier') && normalized.includes('$50')) return 5;
        if (normalized.includes('premier') && normalized.includes('$100')) return 6;
        if (normalized.includes('standard') && normalized.includes('deductible waiver')) return 7;
        if (normalized.includes('standard') && normalized.includes('$50')) return 8;
        if (normalized.includes('standard') && normalized.includes('$100')) return 9;
        if (normalized.includes('powertrain') && normalized.includes('deductible waiver')) return 10;

        // Default for any other products
        return 99;
      };

      const priorityA = getProductPriority(a);
      const priorityB = getProductPriority(b);

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      // If same priority, sort alphabetically
      return a.localeCompare(b);
    };

    // Sort groups by product name and sort term costs within each group
    return Array.from(groups.entries())
      .filter(([productName]) => productName !== 'Other') // Additional filter to ensure no 'Other' groups
      .sort(([a], [b]) => sortVSCProducts(a, b))
      .map(([productName, termCosts]) => ({
        productName,
        termCosts: termCosts.sort((a, b) => {
          // First sort by term (years)
          if (a.termCost.term !== b.termCost.term) {
            return a.termCost.term - b.termCost.term;
          }
          // Then by miles
          return a.termCost.miles - b.termCost.miles;
        })
      }));
  }

}
